import { eq } from "drizzle-orm";
import {
  asset,
  type PowerSyncDrizzleDB,
  reminder_rule,
  service_record,
  usage_reading,
  warranty,
} from "@tendly/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import type { AssetKind, ServiceCategory, UsageUnit } from "./constants";
import { isServiceCategory } from "./constants";
import { computeWarrantyExpiry } from "./due";

/**
 * Local mutations for the Tendly domain. Writes to PowerSync SQLite;
 * the connector uploads them to the server (write-back) in the background.
 * Costs ALWAYS in minor units (cents, integers).
 */

// ─── Assets ───────────────────────────────────────────────────────────────────

export interface AssetDraft {
  name: string;
  kind: AssetKind;
  usageUnit: UsageUnit;
  icon?: string | null;
  photoUrl?: string | null;
  licensePlate?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  purchaseDate?: string | null;
  notes?: string | null;
  sortOrder?: number;
}

export async function createAssetMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: AssetDraft,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(asset).values({
    id,
    user_id: userId,
    name: draft.name.trim(),
    kind: draft.kind,
    usage_unit: draft.usageUnit,
    icon: draft.icon ?? null,
    photo_url: draft.photoUrl ?? null,
    license_plate: draft.licensePlate ?? null,
    make: draft.make ?? null,
    model: draft.model ?? null,
    year: draft.year ?? null,
    purchase_date: draft.purchaseDate ?? null,
    notes: draft.notes ?? null,
    archived: 0,
    sort_order: draft.sortOrder ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateAssetMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    kind: string;
    usage_unit: string;
    icon: string | null;
    photo_url: string | null;
    license_plate: string | null;
    make: string | null;
    model: string | null;
    year: number | null;
    purchase_date: string | null;
    notes: string | null;
    archived: number;
    sort_order: number;
  }>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }
  await patchRowVerified(db, asset, asset.id, id, finalUpdates, options);
}

/**
 * Logical deletion (L2 trash): asset goes to trash and ALL its history
 * (services/costs/readings/warranties/reminders) is preserved intact — no
 * cascade. Reappears completely upon restoration.
 */
export async function deleteAssetMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, asset, asset.id, id, { deleted_at: nowISO() }, options);
}

/** Restores an asset from trash (clears `deleted_at`). Inverse of deletion. */
export async function restoreAssetMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, asset, asset.id, id, { deleted_at: null }, options);
}

/**
 * PERMANENT deletion of an asset (trash → "delete forever", and inverse
 * of "create asset": newly created asset has no history painful to lose).
 * Real DELETE cascades its history via FK on server. NO undo.
 */
export async function hardDeleteAssetMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(asset).where(eq(asset.id, id));
}

// ─── Services ─────────────────────────────────────────────────────────────────

export interface ServiceRecordDraft {
  assetId: string;
  title: string;
  category: ServiceCategory;
  performedAt: string;
  costCents?: number | null;
  currency?: string;
  usageValue?: number | null;
  vendor?: string | null;
  note?: string | null;
  /** Rule that this service "fulfills" → resets (last_done ← service). */
  reminderRuleId?: string | null;
  /** If true and usageValue present, also records a usage reading. */
  recordUsageReading?: boolean;
}

/** Minimal reminder rule to build a quick "mark as done". */
export interface QuickCompleteRuleInput {
  id: string;
  asset_id: string | null;
  title: string | null;
  category: string | null;
  interval_usage: number | null;
}

/**
 * Builds service draft for a quick "Mark as done" on a
 * reminder rule (closes maintenance loop in a single touch, without opening
 * full dialog). Resets rule (last_done_at/_usage) because it carries
 * `reminderRuleId`.
 *
 * - `performedAt` = now (ISO). Service is dated at moment of action.
 * - `usageValue` = last known reading of asset, ONLY if rule has
 *   usage axis (`interval_usage`); so resetting usage threshold starts from
 *   current odometer/hour meter. If reading present, also recorded as
 *   `usage_reading` (recordUsageReading) to preserve data point.
 * - `category` = rule category if valid; otherwise, "maintenance".
 * - Returns `null` if rule has no asset (service cannot be dated).
 */
export function buildQuickCompleteDraft(
  rule: QuickCompleteRuleInput,
  latestUsage: number | null,
  now: Date = new Date(),
): ServiceRecordDraft | null {
  if (!rule.asset_id) {
    return null;
  }
  const usesUsageAxis = rule.interval_usage !== null && rule.interval_usage > 0;
  const usageValue = usesUsageAxis ? latestUsage : null;
  const category: ServiceCategory =
    rule.category && isServiceCategory(rule.category) ? rule.category : "maintenance";
  return {
    assetId: rule.asset_id,
    title: (rule.title ?? "").trim() || "Maintenance done",
    category,
    performedAt: now.toISOString(),
    usageValue,
    reminderRuleId: rule.id,
    recordUsageReading: usageValue !== null,
  };
}

/**
 * Records a service. Atomically (same local queue):
 *  1. inserts service_record,
 *  2. if `reminderRuleId` → rearms rule (last_done_at/last_done_usage),
 *  3. if `recordUsageReading` and reading present → inserts usage_reading.
 */
export async function createServiceRecordMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: ServiceRecordDraft,
  options: VerifiedWriteOptions = {},
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(service_record).values({
    id,
    user_id: userId,
    asset_id: draft.assetId,
    title: draft.title.trim(),
    category: draft.category,
    performed_at: draft.performedAt,
    cost_cents: draft.costCents ?? null,
    currency: draft.currency ?? "EUR",
    usage_value: draft.usageValue ?? null,
    vendor: draft.vendor ?? null,
    note: draft.note ?? null,
    reminder_rule_id: draft.reminderRuleId ?? null,
    created_at: now,
    updated_at: now,
  });

  if (draft.reminderRuleId) {
    // The rule this service rearms may have been created in the SAME
    // session (e.g. "save service + new reminder"): without verification,
    // the rearm UPDATE may match zero rows while that row travels to
    // server — service is recorded but rule remains "due".
    await patchRowVerified(
      db,
      reminder_rule,
      reminder_rule.id,
      draft.reminderRuleId,
      {
        last_done_at: draft.performedAt,
        last_done_usage: draft.usageValue ?? null,
      },
      options,
    );
  }

  if (draft.recordUsageReading && draft.usageValue !== null && draft.usageValue !== undefined) {
    await createUsageReadingMutation(db, userId, {
      assetId: draft.assetId,
      value: draft.usageValue,
      recordedAt: draft.performedAt,
      note: draft.title.trim(),
    });
  }

  return id;
}

export async function updateServiceRecordMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    title: string;
    category: string;
    performed_at: string;
    cost_cents: number | null;
    currency: string;
    usage_value: number | null;
    vendor: string | null;
    note: string | null;
    reminder_rule_id: string | null;
  }>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.title !== undefined) {
    finalUpdates.title = finalUpdates.title.trim();
  }
  await patchRowVerified(db, service_record, service_record.id, id, finalUpdates, options);
}

export async function deleteServiceRecordMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    service_record,
    service_record.id,
    id,
    { deleted_at: nowISO() },
    options,
  );
}

/** Restores a deleted service (inverse of its deletion from the UI). */
export async function restoreServiceRecordMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, service_record, service_record.id, id, { deleted_at: null }, options);
}

/**
 * HARD deletion of a service (inverse of "record service/cost": a newly
 * created record, without own history). Real DELETE, no trash.
 */
export async function hardDeleteServiceRecordMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(service_record).where(eq(service_record.id, id));
}

// ─── Usage readings ──────────────────────────────────────────────────────────

export interface UsageReadingDraft {
  assetId: string;
  value: number;
  recordedAt: string;
  note?: string | null;
}

export async function createUsageReadingMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: UsageReadingDraft,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(usage_reading).values({
    id,
    user_id: userId,
    asset_id: draft.assetId,
    value: Math.round(draft.value),
    recorded_at: draft.recordedAt,
    note: draft.note ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteUsageReadingMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(usage_reading).where(eq(usage_reading.id, id));
}

// ─── Reminder rules ───────────────────────────────────────────────────

export interface ReminderRuleDraft {
  assetId: string;
  title: string;
  category: ServiceCategory;
  intervalMonths?: number | null;
  intervalUsage?: number | null;
  lastDoneAt?: string | null;
  lastDoneUsage?: number | null;
  leadDays?: number;
  note?: string | null;
}

export async function createReminderRuleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: ReminderRuleDraft,
): Promise<string> {
  if (!draft.intervalMonths && !draft.intervalUsage) {
    throw new Error("reminder_rule requires interval_months or interval_usage");
  }
  const id = generateId();
  const now = nowISO();
  const DEFAULT_LEAD_DAYS = 14;
  await db.insert(reminder_rule).values({
    id,
    user_id: userId,
    asset_id: draft.assetId,
    title: draft.title.trim(),
    category: draft.category,
    interval_months: draft.intervalMonths ?? null,
    interval_usage: draft.intervalUsage ?? null,
    last_done_at: draft.lastDoneAt ?? null,
    last_done_usage: draft.lastDoneUsage ?? null,
    lead_days: draft.leadDays ?? DEFAULT_LEAD_DAYS,
    active: 1,
    note: draft.note ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateReminderRuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    title: string;
    category: string;
    interval_months: number | null;
    interval_usage: number | null;
    last_done_at: string | null;
    last_done_usage: number | null;
    lead_days: number;
    active: number;
    note: string | null;
  }>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.title !== undefined) {
    finalUpdates.title = finalUpdates.title.trim();
  }
  await patchRowVerified(db, reminder_rule, reminder_rule.id, id, finalUpdates, options);
}

export async function deleteReminderRuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    reminder_rule,
    reminder_rule.id,
    id,
    { deleted_at: nowISO() },
    options,
  );
}

// ─── Warranties ───────────────────────────────────────────────────────────────

export interface WarrantyDraft {
  assetId: string;
  title: string;
  purchaseDate: string;
  durationMonths: number;
  vendor?: string | null;
  note?: string | null;
}

export async function createWarrantyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: WarrantyDraft,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(warranty).values({
    id,
    user_id: userId,
    asset_id: draft.assetId,
    title: draft.title.trim(),
    purchase_date: draft.purchaseDate,
    duration_months: draft.durationMonths,
    expires_at: computeWarrantyExpiry(draft.purchaseDate, draft.durationMonths).toISOString(),
    vendor: draft.vendor ?? null,
    note: draft.note ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateWarrantyMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    title: string;
    purchase_date: string;
    duration_months: number;
    vendor: string | null;
    note: string | null;
  }>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const finalUpdates: Record<string, unknown> = { ...updates };
  if (typeof finalUpdates.title === "string") {
    finalUpdates.title = finalUpdates.title.trim();
  }
  if (updates.purchase_date !== undefined && updates.duration_months !== undefined) {
    finalUpdates.expires_at = computeWarrantyExpiry(
      updates.purchase_date,
      updates.duration_months,
    ).toISOString();
  }
  await patchRowVerified(db, warranty, warranty.id, id, finalUpdates, options);
}

export async function deleteWarrantyMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, warranty, warranty.id, id, { deleted_at: nowISO() }, options);
}

/** Restores a deleted warranty (inverse of its deletion from the UI). */
export async function restoreWarrantyMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, warranty, warranty.id, id, { deleted_at: null }, options);
}

/** HARD deletion of a warranty (inverse of "add warranty"). */
export async function hardDeleteWarrantyMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(warranty).where(eq(warranty.id, id));
}
